import { gql, useApolloClient, useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import { Flex } from 'rebass';
import Button from '../../../../components/Button/Button';
import PropTypes from 'prop-types';
import { Label } from '@rebass/forms';
import useNotify from '../../../../hooks/useNotify';
import { WaterSiteType } from '../../../../components/WaterSite/WaterSiteTypePicker';
import WaterSitePicker from '../../../../components/WaterSite/WaterSitePicker';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';

type SetWaterSiteModalProps = {
  onClose: (actionTaken?: boolean) => void;
  serviceIds: string[] | readonly string[];
  siteType: WaterSiteType;
};

const SET_RECYCLED_WATER_SITE = gql`
  mutation UpdateWaterServicesRecycledWaterSite(
    $waterServiceIds: [ID!]!
    $waterSiteId: ID
  ) {
    updateWaterServicesRecycledWaterSite(
      waterServiceIds: $waterServiceIds
      waterSiteId: $waterSiteId
    ) {
      id
      recycledWaterSiteId
      recycledWaterSite {
        id
        name
      }
    }
  }
`;

const SET_HAZARD_SITE = gql`
  mutation UpdateWaterServicesRecycledWaterSite(
    $waterServiceIds: [ID!]!
    $waterSiteId: ID
  ) {
    updateWaterServicesHazardSite(
      waterServiceIds: $waterServiceIds
      waterSiteId: $waterSiteId
    ) {
      id
      recycledWaterSiteId
      recycledWaterSite {
        id
        name
      }
    }
  }
`;

const GET_WATER_SITES = gql`
  query GetWaterSites(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    waterSites(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      id
      name
      address {
        id
        street1
      }
    }
  }
`;

const SetWaterSiteModal: React.FC<SetWaterSiteModalProps> = ({
  onClose,
  serviceIds: ids,
  siteType,
}) => {
  const [queryLoading, setQueryLoading] = useState(true);
  const [queryError, setQueryError] = useState(undefined as Error | undefined);
  const [waterSites, setWaterSites] = useState([] as any[]);
  const client = useApolloClient();
  const { showError, showSuccess } = useNotify();

  const loadWaterSites = () => {
    setQueryLoading(true);
    client
      .query<{
        waterSites: {
          id: string;
          name: string;
          address: { id: string; street1: string };
        }[];
      }>({
        query: GET_WATER_SITES,
        variables: {
          limit: null,
        },
        fetchPolicy: 'no-cache',
      })
      .then((result) => {
        setWaterSites(
          result.data.waterSites.map((waterSite) => {
            return {
              id: waterSite.id,
              name: waterSite.name + ' - ' + waterSite.address.street1,
            };
          })
        );
      })
      .catch((e: React.SetStateAction<Error | undefined>) => {
        setQueryError(e);
      })
      .finally(() => {
        setQueryLoading(false);
      });
  };

  useEffect(loadWaterSites, []);

  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => {
    showSuccess(
      `${ids.length} water service${ids.length > 1 ? 's' : ''} updated`
    );
    onClose(true);
  }, []);

  const [setWaterSite, { loading: mutationLoading }] = useMutation(
    siteType === 'HAZARD_ASSESSMENT'
      ? SET_HAZARD_SITE
      : SET_RECYCLED_WATER_SITE,
    {
      onCompleted: onActionComplete,
      onError: useCallback((error) => {
        showError(
          `Error updating water service${ids.length > 1 ? 's' : ''}`,
          error
        );
      }, []),
    }
  );

  const [waterSiteId, setWaterSiteId] = useState<string | undefined>(undefined);

  return (
    <Modal title={`Set ${constantToLabel(siteType)} Site`} onClose={onCancel}>
      <ModalBody>
        <Flex p={3} flexDirection="column">
          {queryLoading && <p>Loading...</p>}
          {queryError && <p>Error :( {queryError.message}</p>}
          {!queryLoading && !queryError && (
            <>
              <Label htmlFor="site">Select Site</Label>
              <WaterSitePicker
                autoFocus
                siteType={siteType}
                waterSiteId={waterSiteId}
                onChange={(waterSite) => setWaterSiteId(waterSite?.id)}
              />
            </>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={mutationLoading}
          onClick={() => {
            if (!waterSiteId) {
              showError('Please choose a water site');
              return;
            }
            setWaterSite({
              variables: {
                waterServiceIds: ids,
                waterSiteId: waterSiteId || null,
              },
            });
          }}
        >
          Save
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SetWaterSiteModal.propTypes = {
  onClose: PropTypes.any,
  serviceIds: PropTypes.any.isRequired,
};

export default SetWaterSiteModal;
